/*
 * Copyright 2026 Sony Corporation
 */

import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SendIcon from '@mui/icons-material/Send';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import { useEffect, useState } from 'react';

import { CollapsedRail } from '../panels/CollapsedRail';
import { MessageList } from './MessageList';
import { useAgent, type ApplyOps } from '../../hooks/use-agent';
import type { AgentOp, RequestOps } from '../../services/agent';

/**
 * A second view sharing the panel, switched from its head.
 *
 * The page owns which one is showing, because selecting a device in the
 * viewport is also a request to look at it.
 */
export interface AlternateView {
  label: string;
  content: React.ReactNode;
  showing: boolean;
  onShow: (showing: boolean) => void;
}

export interface ChatPanelProps<Op extends AgentOp, Digest> {
  /** What the assistant is told about the page, read when a turn resolves. */
  digest: () => Digest;
  apply: ApplyOps<Op>;
  /** Phase 1 passes a scripted stub; P3/P4 pass the real agent and nothing here changes. */
  requestOps: RequestOps<Op, Digest>;
  /** What the stub behind it knows, offered rather than left to be guessed. */
  suggestions: readonly string[];
  /** What the box is for, in this page's terms. */
  placeholder: string;
  needsConfirmation?: (ops: readonly Op[]) => boolean;
  /** Ops waiting for a click, so the page can preview them in the viewport. */
  onPending?: (ops: readonly Op[]) => void;
  /** A real agent keeps the transcript too, so it has to be told to forget it. */
  onNewThread?: () => void;
  /** The turn being written. A turn takes seconds, and a panel showing nothing looks broken. */
  streaming?: string;
  alternate?: AlternateView;
}

/**
 * The assistant, in a column on the right.
 *
 * The same panel on both pages rather than a column here and a dock there: they
 * are one interaction, and the difference the devplan sketched bought two
 * layouts to maintain and no answer the single one does not give (devplan §12,
 * U4). What changes between the pages is the op vocabulary, which is a prop.
 *
 * A page may hang a second view off the same column — the Device page puts its
 * inspector there. The two share one slot because they are read the same way,
 * one at a time, about the same thing.
 */
export const ChatPanel = <Op extends AgentOp, Digest>({
  digest,
  apply,
  requestOps,
  suggestions,
  placeholder,
  needsConfirmation,
  onPending,
  onNewThread,
  streaming,
  alternate,
}: ChatPanelProps<Op, Digest>) => {
  const [open, setOpen] = useState(true);
  const [draft, setDraft] = useState('');

  const {
    messages,
    pending,
    busy,
    error,
    send,
    apply: applyMessage,
    discard,
    newThread,
  } = useAgent({
    digest,
    apply,
    requestOps,
    needsConfirmation,
  });

  useEffect(() => {
    onPending?.(pending);
  }, [pending, onPending]);

  const submit = (prompt: string) => {
    if (busy) {
      return;
    }
    send(prompt);
    setDraft('');
  };

  const showing = alternate?.showing === true;
  const label = showing && alternate ? alternate.label : 'Chat';

  if (!open) {
    return (
      <Box className="absolute inset-y-0 right-0 z-10">
        <CollapsedRail side="right" label={label} translucent onExpand={() => setOpen(true)} />
      </Box>
    );
  }

  return (
    <Box
      // Over the viewport rather than beside it: the scene keeps its full width
      // and stays visible through the panel, which is what makes a long thread
      // tolerable next to a 3D view.
      className="absolute inset-y-0 right-0 z-10 flex w-80 flex-col border-l"
      sx={(theme) => ({
        bgcolor: alpha(theme.palette.background.paper, 0.6),
        backdropFilter: 'blur(8px)',
        borderColor: 'divider',
      })}
    >
      <Box className="flex items-center justify-between gap-1 px-3 py-2">
        {alternate ? (
          <ToggleButtonGroup
            size="small"
            value={showing ? 'alternate' : 'chat'}
            exclusive
            onChange={(_event, next: string | null) => next && alternate.onShow(next === 'alternate')}
          >
            <ToggleButton value="chat" className="px-2 py-0.5 text-xs">
              Chat
            </ToggleButton>
            <ToggleButton value="alternate" className="px-2 py-0.5 text-xs">
              {alternate.label}
            </ToggleButton>
          </ToggleButtonGroup>
        ) : (
          <Typography variant="subtitle2">Chat</Typography>
        )}
        <Box className="flex items-center gap-1">
          {!showing && (
            <Button
              size="small"
              startIcon={<AddIcon />}
              disabled={messages.length === 0}
              onClick={() => {
                onNewThread?.();
                newThread();
              }}
            >
              New thread
            </Button>
          )}
          <Tooltip title={`Hide ${label.toLowerCase()}`}>
            <IconButton size="small" aria-label={`Hide ${label.toLowerCase()}`} onClick={() => setOpen(false)}>
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {showing && alternate?.content}

      {/* Hidden rather than unmounted: switching away from a conversation must
          not end it. */}
      <Box className={showing ? 'hidden' : 'flex min-h-0 flex-1 flex-col'}>
        {messages.length === 0 ? (
          <Box className="flex flex-1 flex-col items-center justify-center gap-2 px-6 text-center">
            <AutoAwesomeOutlinedIcon sx={{ color: 'text.disabled' }} />
            <Typography variant="body2" color="text.secondary">
              {placeholder}
            </Typography>
            {/* Worked examples rather than a feature list, and they are exactly
              what the scripted stub knows — so the offer is never a promise it
              cannot keep. */}
            {suggestions.map((prompt) => (
              <Chip
                key={prompt}
                label={prompt}
                size="small"
                variant="outlined"
                onClick={() => submit(prompt)}
                sx={{ height: 'auto', py: 0.5, '& .MuiChip-label': { whiteSpace: 'normal', fontSize: 12 } }}
              />
            ))}
          </Box>
        ) : (
          <MessageList
            messages={messages}
            busy={busy}
            streaming={streaming}
            onApply={applyMessage}
            onDiscard={discard}
          />
        )}

        <Box className="flex flex-col gap-2 px-3 pb-3">
          {error && (
            <Alert severity="error" variant="outlined">
              {error}
            </Alert>
          )}
          <TextField
            size="small"
            fullWidth
            multiline
            maxRows={4}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              // Enter sends, Shift+Enter breaks the line: a prompt is usually one.
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                submit(draft);
              }
            }}
            placeholder="Ask for a change…"
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label="Send"
                      disabled={busy || draft.trim() === ''}
                      onClick={() => submit(draft)}
                    >
                      <SendIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};
